import { updateDocument, queryDocuments } from './dbService';
import { AppStatus } from '../constants/appStatus';
import { arrayUnion, serverTimestamp } from 'firebase/firestore';

/**
 * Updates an app's status with history tracking
 * @param {string} appId - The app document ID
 * @param {string} newStatus - The new status from AppStatus enum
 * @param {string} notes - Optional notes about status change
 * @returns {Promise<void>}
 */
export const updateAppStatus = async (appId, newStatus, notes = '') => {
  try {
    console.log(`=== DEBUG updateAppStatus: Updating app ${appId} to status ${newStatus} ===`);
    
    if (!appId) {
      console.error('DEBUG updateAppStatus: Error - appId is empty or null');
      return false;
    }
    
    if (!newStatus) {
      console.error('DEBUG updateAppStatus: Error - newStatus is empty or null');
      return false;
    }
    
    // שימוש ב-serverTimestamp רק לשדה lastStatusUpdate
    const serverTime = serverTimestamp();
    
    // עבור ה-arrayUnion משתמשים בתאריך JavaScript רגיל
    const jsTimestamp = new Date().toISOString();
    
    const statusEntry = {
      status: newStatus,
      timestamp: jsTimestamp, // שימוש בתאריך רגיל, לא ב-serverTimestamp
      notes: notes || ''
    };
    
    console.log('DEBUG updateAppStatus: Status entry to add:', statusEntry);
    console.log('DEBUG updateAppStatus: Calling updateDocument...');
    
    await updateDocument('userApps', appId, {
      statusApp: newStatus,
      lastStatusUpdate: serverTime, // כאן אפשר להשתמש ב-serverTimestamp
      // Add to start of history array using arrayUnion
      statusHistory: arrayUnion(statusEntry)
    });
    
    console.log('DEBUG updateAppStatus: Document updated successfully');
    return true;
  } catch (error) {
    console.error('DEBUG updateAppStatus: Error updating app status:', error);
    console.error('DEBUG updateAppStatus: Error stack:', error.stack);
    throw error;
  }
};

/**
 * Migrates all user apps to the new status format (DEPRECATED - Migration script removed)
 * @returns {Promise<{success: number, failed: number}>}
 */
export const migrateAllAppsToNewStatus = async () => {
  console.warn('migrateAllAppsToNewStatus is deprecated and should not be used.');
  return { success: 0, failed: 0 }; // Return empty result
  /* Original migration logic removed
  try {
    const userApps = await queryDocuments('userApps', 'userId', '!=', null);
    let successCount = 0;
    let failCount = 0;
    
    for (const app of userApps) {
      try {
        // Skip if already migrated
        if (app.statusApp) continue;
        
        const newStatus = mapOldStatusToNew(app.status);
        const timestamp = app.lastUpdated || app.uploadedAt || serverTimestamp();
        
        await updateDocument('userApps', app.id, {
          statusApp: newStatus,
          lastStatusUpdate: timestamp,
          statusHistory: [{
            status: newStatus,
            timestamp: timestamp,
            notes: 'Migrated from legacy status'
          }]
        });
        
        successCount++;
      } catch (error) {
        console.error(`Error migrating app ${app.id}:`, error);
        failCount++;
      }
    }
    
    return { success: successCount, failed: failCount };
  } catch (error) {
    console.error('Error in migration process:', error);
    throw error;
  }
  */
};

/**
 * Gets user apps with additional status info
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - List of user apps with status info
 */
export const getUserAppsWithStatus = async (userId) => {
  try {
    const userApps = await queryDocuments('userApps', 'userId', '==', userId);
    
    return userApps.map(app => {
      // Use statusApp, default to AWAITING_UPLOAD if missing
      const currentStatus = app.statusApp || AppStatus.AWAITING_UPLOAD;
      
      return {
        ...app,
        statusApp: currentStatus,
        // Make sure we always have a statusHistory
        statusHistory: app.statusHistory || []
      };
    });
  } catch (error) {
    console.error('Error getting user apps with status:', error);
    throw error;
  }
}; 